import type { Child } from "@spork/s4p-server-sdk";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Accordion } from "@/components/ui/accordion";
import { getChildLabel, showChildLabels } from "@/features/selectedChild";
import { useGetMedicalRequestsForChildrenQuery } from "@/query/apiSlice";
import { TEST_IDS } from "@/testIdDefinition";

import { Text } from "@/components/ui/text";
import { View } from "react-native";
import Animated, { LinearTransition } from "react-native-reanimated";
import { Badge } from "../../components/ui/badge";
import { Section } from "../../components/ui/section";
import { MedicalRequestHomeCard } from "./MedicalRequestHomeCard";

const LAYOUT_TRANSITION = LinearTransition.duration(200);

type ActiveMedicalRequestsSectionProps = {
    childList: Child[];
};

export function ActiveMedicalRequestsSection({
    childList,
}: ActiveMedicalRequestsSectionProps) {
    const { t } = useTranslation();
    const childIds = useMemo(
        () => childList.map((child) => child.id),
        [childList],
    );

    const { data: medicalRequests = [] } =
        useGetMedicalRequestsForChildrenQuery(childIds, {
            skip: childIds.length === 0,
        });

    const childById = useMemo(() => {
        const map = new Map<number, Child>();
        for (const child of childList) {
            map.set(child.id, child);
        }
        return map;
    }, [childList]);

    const visibleRequests = useMemo(
        () =>
            medicalRequests.filter(
                (request) =>
                    request.is_valid && childById.has(request.child_id),
            ),
        [medicalRequests, childById],
    );

    if (visibleRequests.length === 0) {
        return null;
    }

    const title =
        visibleRequests.length > 1
            ? t("home.medicalNotesCount", {
                  count: visibleRequests.length,
              })
            : t("home.medicalNoteCount");

    const sectionTitle = (
        <View className="flex-row items-center gap-2">
            <Text
                variant="caption"
                className="px-1 uppercase tracking-wide"
                testID={TEST_IDS[TEST_IDS.HOME_MEDICAL_REQUESTS_SECTION]}
            >
                {title}
            </Text>
            <Badge variant="default">
                <Text>{visibleRequests.length}</Text>
            </Badge>
        </View>
    );
    return (
        <Animated.View layout={LAYOUT_TRANSITION}>
            <Accordion type="single" collapsible>
                <Section title={sectionTitle}>
                    {visibleRequests.map((request) => {
                        const child = childById.get(request.child_id);
                        if (!child) {
                            return null;
                        }
                        return (
                            <MedicalRequestHomeCard
                                key={request.id}
                                request={request}
                                childLabel={
                                    showChildLabels(childList.length)
                                        ? getChildLabel(child)
                                        : undefined
                                }
                            />
                        );
                    })}
                </Section>
            </Accordion>
        </Animated.View>
    );
}
